import { NewMessageEvent } from '@/hooks/useSocket';
import { InboxItem } from '@/services/chat';
import { Chat } from '@/stores/slices/chat/chat.types';

const avatarRandom =
  'https://ia801308.us.archive.org/8/items/whatsapp-smiling-guy-i-accidentally-made/whatsapp%20generic%20person%20light.jpg';

/**
 * Maps a chat response from the backend (InboxItem) to the UI Chat props.
 *
 * @param {InboxItem} chat - The chat response object from the backend.
 * @param {Chat[]} existingChats - The current list of chats in the UI state.
 * @returns {Chat} The mapped Chat object for UI consumption.
 */
export const mapChatResponseToUIProps = (
  chat: InboxItem,
  existingChats: Chat[]
): Chat => {
  const existingChat = existingChats.find((c) => c.id === chat.contact.id);
  return {
    id: chat.contact.id,
    botId: chat.inbox_last_message.bot_id,
    avatar: chat.contact.channel_data.photo || avatarRandom,
    alt: chat.contact.channel_data.name || 'Unknown',
    title: chat.contact.channel_data.name,
    subtitle: chat.inbox_last_message.data.text?.body,
    date: new Date(chat.inbox_last_message.created_at),
    unread: chat.inbox_unread,
    messages: existingChat ? existingChat.messages : []
  };
};

/**
 * Maps a new chat from a message event to the Chat type.
 *
 * @param {NewMessageEvent} data - The new message event data.
 * @returns {Chat} The mapped Chat object for UI consumption.
 */
export const mapNewChatFromMessage = (data: NewMessageEvent): Chat => {
  const newMessage = {
    id: data.messageDetails?.id,
    position: data.sendPulseData?.direction === 1 ? 'left' : 'right',
    date: new Date(data.messageDetails?.createdAt || data.timestamp),
    status: data.messageDetails?.status || 1, // Assuming 1 is a default status
    title: data.messageDetails?.direction === 1 ? data.message.from : 'You',
    type: 'text',
    text: data.message.text,
    reply: undefined
  };

  return {
    id: data.contactId,
    botId: data.botId,
    avatar: avatarRandom,
    alt: data.message.text || 'Unknown',
    title: data.message.from || 'Unknown',
    subtitle: data.message.text || 'No message text',
    date: new Date(data.timestamp),
    unread: 1,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    messages: [newMessage]
  };
};
